/**
 * File: assets/basari-360-viewer.js
 * Purpose: Open the Basari 360 modal and initialize the existing Magic 360 runtime.
 * Owner: Basari Theme / PDP
 * Dependencies: window.Magic360 from the Shopify Magic 360 app embed
 * Notes: Does not load a second Magic 360 library. Does not touch the Basari gallery.
 */
(function () {
  'use strict';

  var OPEN_CLASS = 'basari-360-modal-open';
  var MAGIC_WAIT_MS = 100;
  var MAGIC_WAIT_MAX = 40;
  var SIZE_WAIT_MS = 50;
  var SIZE_WAIT_MAX = 20;

  function parseFrames(modal) {
    var node = modal.querySelector('[data-basari-360-frames]');
    if (!node || !node.textContent) return [];
    try {
      var parsed = JSON.parse(node.textContent);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(function (url) {
        return typeof url === 'string' && url.length > 0;
      });
    } catch (error) {
      return [];
    }
  }

  function parseSpinFrameNumber(src) {
    var file = String(src || '')
      .split('?')[0]
      .split('/')
      .pop()
      .toLowerCase();
    var match = file.match(/^frame[-_](\d+)[._]/);
    if (!match) return 0;
    var frameNumber = parseInt(match[1], 10);
    if (!frameNumber || frameNumber < 1) return 0;
    return frameNumber;
  }

  function parseFramesFromProductMedia(root) {
    var node = root.querySelector('[data-basari-variants-json]');
    if (!node || !node.textContent) return [];
    var data;
    try {
      data = JSON.parse(node.textContent);
    } catch (error) {
      return [];
    }
    var media = data && data.media ? data.media : [];
    var byNumber = {};
    var i;
    for (i = 0; i < media.length; i += 1) {
      var src = media[i] && media[i].src ? String(media[i].src) : '';
      if (!src) continue;
      var frameNumber = parseSpinFrameNumber(src);
      if (frameNumber < 1 || byNumber[frameNumber]) continue;
      if (src.indexOf('//') === 0) src = 'https:' + src;
      byNumber[frameNumber] = src;
    }
    var numbers = Object.keys(byNumber)
      .map(function (key) {
        return parseInt(key, 10);
      })
      .filter(function (num) {
        return num >= 1;
      })
      .sort(function (a, b) {
        return a - b;
      });
    var ordered = [];
    for (i = 0; i < numbers.length; i += 1) {
      ordered.push(byNumber[numbers[i]]);
    }
    return ordered;
  }

  function framesAreOrdered(frames) {
    var last = 0;
    var seen = {};
    var i;
    for (i = 0; i < frames.length; i += 1) {
      var frameNumber = parseSpinFrameNumber(frames[i]);
      if (!frameNumber || frameNumber <= last || seen[frameNumber]) return false;
      seen[frameNumber] = true;
      last = frameNumber;
    }
    return true;
  }

  function getMagic360() {
    var magic = window.Magic360;
    if (!magic || typeof magic.start !== 'function') return null;
    return magic;
  }

  function waitFor(check, interval, maxAttempts, done) {
    var attempts = 0;
    function tick() {
      if (check()) {
        done(true);
        return;
      }
      attempts += 1;
      if (attempts >= maxAttempts) {
        done(false);
        return;
      }
      window.setTimeout(tick, interval);
    }
    tick();
  }

  function countMountSpins(mount) {
    if (!mount) return 0;
    return mount.querySelectorAll('.Magic360, .Magic360-container').length;
  }

  function Basari360Viewer(root) {
    this.root = root;
    this.button = root.querySelector('[data-basari-viewer-360-button]');
    this.modal = root.querySelector('[data-basari-360-modal]');
    if (!this.button || !this.modal) return;

    this.panel = this.modal.querySelector('[data-basari-360-panel]');
    this.mount = this.modal.querySelector('[data-basari-360-mount]');
    this.viewer = this.modal.querySelector('[data-basari-360-viewer]');
    this.closeButtons = this.modal.querySelectorAll('[data-basari-360-close]');
    this.viewerId = this.modal.getAttribute('data-basari-360-viewer-id') || '';
    this.frames = parseFrames(this.modal);
    if (this.frames.length < 2 || !framesAreOrdered(this.frames)) {
      this.frames = parseFramesFromProductMedia(root);
    }
    this.ready = this.frames.length >= 2;
    this.altText = this.root.getAttribute('aria-label') || '';
    this.mounted = false;
    this.opening = false;
    this.bound = false;

    this.onButtonClick = this.handleButtonClick.bind(this);
    this.onCloseClick = this.handleCloseClick.bind(this);
    this.onDialogClick = this.handleDialogClick.bind(this);
    this.onDialogClose = this.handleDialogClose.bind(this);
    this.onDialogCancel = this.handleDialogCancel.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onAnchorClick = this.handleAnchorClick.bind(this);

    this.bind();
  }

  Basari360Viewer.prototype.bind = function () {
    if (this.bound || !this.button || !this.modal) return;
    this.bound = true;

    if (this.modal.id) {
      this.button.setAttribute('aria-controls', this.modal.id);
    }
    this.button.setAttribute('aria-haspopup', 'dialog');
    this.button.setAttribute('aria-expanded', 'false');

    this.button.addEventListener('click', this.onButtonClick);

    var i;
    for (i = 0; i < this.closeButtons.length; i += 1) {
      this.closeButtons[i].addEventListener('click', this.onCloseClick);
    }

    this.modal.addEventListener('click', this.onDialogClick);
    this.modal.addEventListener('close', this.onDialogClose);
    this.modal.addEventListener('cancel', this.onDialogCancel);
  };

  Basari360Viewer.prototype.handleButtonClick = function (event) {
    event.preventDefault();
    this.open();
  };

  Basari360Viewer.prototype.handleCloseClick = function (event) {
    event.preventDefault();
    this.close();
  };

  Basari360Viewer.prototype.handleDialogClick = function (event) {
    if (event.target === this.modal) {
      this.close();
    }
  };

  Basari360Viewer.prototype.handleDialogCancel = function (event) {
    event.preventDefault();
    this.close();
  };

  Basari360Viewer.prototype.handleKeydown = function (event) {
    if (event.key === 'Escape' && this.isOpen()) {
      event.preventDefault();
      this.close();
    }
  };

  Basari360Viewer.prototype.handleAnchorClick = function (event) {
    event.preventDefault();
  };

  Basari360Viewer.prototype.handleDialogClose = function () {
    this.afterClose();
  };

  Basari360Viewer.prototype.isOpen = function () {
    return !!(this.modal && this.modal.open);
  };

  Basari360Viewer.prototype.open = function () {
    if (!this.modal || this.isOpen() || this.opening) return;
    this.opening = true;
    this.button.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add(OPEN_CLASS);
    document.addEventListener('keydown', this.onKeydown, true);

    if (typeof this.modal.showModal === 'function') {
      this.modal.showModal();
    } else {
      this.modal.setAttribute('open', '');
    }

    var self = this;
    this.whenUsable(function () {
      self.opening = false;
      if (!self.isOpen()) return;
      self.ensureViewer();
    });
  };

  Basari360Viewer.prototype.close = function () {
    if (!this.modal || !this.isOpen()) return;
    if (typeof this.modal.close === 'function') {
      this.modal.close();
    } else {
      this.modal.removeAttribute('open');
      this.afterClose();
    }
  };

  Basari360Viewer.prototype.afterClose = function () {
    this.opening = false;
    this.stopViewer();
    this.button.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove(OPEN_CLASS);
    document.removeEventListener('keydown', this.onKeydown, true);
    if (this.button && typeof this.button.focus === 'function') {
      this.button.focus();
    }
  };

  Basari360Viewer.prototype.whenUsable = function (done) {
    var self = this;
    waitFor(
      function () {
        return !!getMagic360();
      },
      MAGIC_WAIT_MS,
      MAGIC_WAIT_MAX,
      function (magicReady) {
        if (!magicReady || !self.ready) {
          done();
          return;
        }
        waitFor(
          function () {
            var node = self.viewer || self.mount;
            if (!node) return false;
            var rect = node.getBoundingClientRect();
            return rect.width > 1 && rect.height > 1;
          },
          SIZE_WAIT_MS,
          SIZE_WAIT_MAX,
          function () {
            done();
          }
        );
      }
    );
  };

  Basari360Viewer.prototype.ensureViewer = function () {
    if (!this.ready || !this.mount || !this.viewerId) return;
    if (!getMagic360()) return;

    if (!this.mounted || !this.hasMountedViewer()) {
      this.mountViewer();
    }

    this.dedupeMount();
    this.startViewer();
  };

  Basari360Viewer.prototype.hasMountedViewer = function () {
    if (!this.mount) return false;
    if (this.viewerId && this.mount.querySelector('[id="' + this.viewerId + '"]')) return true;
    return !!this.mount.querySelector('.Magic360');
  };

  Basari360Viewer.prototype.mountViewer = function () {
    if (!this.mount || this.frames.length < 2 || !this.viewerId) return;

    this.mount.innerHTML = '';

    var columns = this.frames.length;
    var anchor = document.createElement('a');
    anchor.className = 'Magic360';
    anchor.id = this.viewerId;
    anchor.href = '#';
    anchor.setAttribute(
      'data-options',
      'images: ' + this.frames.join(' ') + '; columns: ' + columns + '; autospin: once; hint: true;'
    );
    anchor.addEventListener('click', this.onAnchorClick);

    var img = document.createElement('img');
    img.src = this.frames[0];
    img.alt = this.altText;
    img.draggable = false;
    anchor.appendChild(img);

    this.mount.appendChild(anchor);
    this.mounted = true;
  };

  Basari360Viewer.prototype.dedupeMount = function () {
    if (!this.mount) return;
    var spins = this.mount.querySelectorAll('.Magic360');
    var kept = this.viewerId ? document.getElementById(this.viewerId) : null;
    var i;
    if (kept && !this.mount.contains(kept)) kept = null;
    for (i = 0; i < spins.length; i += 1) {
      if (!kept) {
        kept = spins[i];
      } else if (spins[i] !== kept && spins[i].parentNode) {
        spins[i].parentNode.removeChild(spins[i]);
      }
    }
  };

  Basari360Viewer.prototype.startViewer = function () {
    var magic = getMagic360();
    if (!magic || !this.viewerId || !this.mounted) return;

    try {
      magic.start(this.viewerId);
    } catch (error) {
      this.recreateViewer();
      return;
    }

    this.dedupeMount();
    if (countMountSpins(this.mount) === 0) {
      this.recreateViewer();
    }
  };

  Basari360Viewer.prototype.stopViewer = function () {
    var magic = window.Magic360;
    if (!magic || typeof magic.stop !== 'function' || !this.viewerId) return;
    try {
      magic.stop(this.viewerId);
    } catch (error) {
      /* Fail closed: keep the PDP usable even if stop() is unavailable. */
    }
  };

  Basari360Viewer.prototype.recreateViewer = function () {
    this.stopViewer();
    this.mounted = false;
    if (this.mount) this.mount.innerHTML = '';
    this.mountViewer();
    var magic = getMagic360();
    if (!magic || !this.viewerId || !this.mounted) return;
    try {
      magic.start(this.viewerId);
    } catch (error) {
      this.mounted = false;
    }
    this.dedupeMount();
  };

  function boot() {
    var roots = document.querySelectorAll('[data-basari-product]');
    var i;
    for (i = 0; i < roots.length; i += 1) {
      if (roots[i].querySelector('[data-basari-360-modal]')) {
        new Basari360Viewer(roots[i]);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
